/**
 * Hook for fetching connected accounts across providers.
 *
 * Merges Google accounts (via useGmailStatus) and Microsoft accounts
 * (via useOutlookStatus) into a single list with a unified interface.
 */

import { useMemo } from 'react';

import { useGmailStatus } from './google';
import { useOutlookStatus } from './microsoft';

export type AccountProvider = 'google' | 'microsoft' | 'other';

export interface ConnectedAccount {
  id: number;
  email: string;
  is_primary: boolean;
  provider?: AccountProvider;
  connection_status?: string;
}

interface UseConnectedAccountsOptions {
  orgId?: string | number;
  autoLoad?: boolean;
}

export function useConnectedAccounts(
  options: UseConnectedAccountsOptions = {}
) {
  const { orgId, autoLoad = true } = options;
  const orgIdStr = orgId?.toString();
  const enabled = autoLoad !== false && !!orgId;

  const {
    data: gmailData,
    isLoading: gmailLoading,
    error: gmailError,
    refetch: gmailRefetch,
  } = useGmailStatus(orgIdStr, { enabled });

  const {
    data: outlookData,
    isLoading: outlookLoading,
    error: outlookError,
    refetch: outlookRefetch,
  } = useOutlookStatus(orgIdStr, { enabled });

  const accounts = useMemo(() => {
    const result: ConnectedAccount[] = [];

    // Google accounts. Fail closed: a missing/null status is unreadable, not
    // confirmed-connected — identical to the Microsoft branch below.
    if (gmailData?.accounts) {
      for (const acc of gmailData.accounts) {
        if (
          acc.social_account_id !== null &&
          acc.connection_status === 'connected'
        ) {
          result.push({
            id: acc.social_account_id,
            email: acc.email ?? '',
            is_primary: acc.is_primary,
            provider: 'google' as const,
            connection_status: acc.connection_status,
          });
        }
      }
    }

    // Microsoft accounts
    if (outlookData?.accounts) {
      for (const acc of outlookData.accounts) {
        if (
          acc.social_account_id !== null &&
          acc.outlook_mail_connection_status === 'connected'
        ) {
          result.push({
            id: acc.social_account_id,
            email: acc.email ?? '',
            is_primary: acc.is_primary,
            provider: 'microsoft' as const,
            connection_status: acc.outlook_mail_connection_status ?? undefined,
          });
        }
      }
    }

    return result;
  }, [gmailData?.accounts, outlookData?.accounts]);

  const hasMultipleAccounts = accounts.length > 1;
  const primaryAccount =
    accounts.find(a => a.is_primary) ?? accounts[0] ?? null;

  const reload = useMemo(
    () => async () => {
      await Promise.all([gmailRefetch(), outlookRefetch()]);
    },
    [gmailRefetch, outlookRefetch]
  );

  return {
    accounts,
    loading: gmailLoading || outlookLoading,
    error: gmailError?.message ?? outlookError?.message ?? null,
    reload,
    hasMultipleAccounts,
    primaryAccount,
  };
}
